import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { Upload, Download, FileSpreadsheet, Loader2, AlertCircle, CheckCircle2, X } from 'lucide-react';
import employeeService from '../../../services/employeeService';

const EmployeeImportModal = ({ isOpen, onClose, onImportSuccess }) => {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [error, setError] = useState(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.name.endsWith('.csv')) {
                setFile(selectedFile);
                setError(null);
                setUploadResult(null);
            } else {
                setError('Please upload a valid CSV file (.csv)');
                setFile(null);
            }
        }
    };

    const handleDownloadTemplate = async () => {
        setError(null);
        try {
            const result = await employeeService.downloadTemplate();
            if (!result.success) {
                setError(result.error);
            }
        } catch (err) {
            setError('Failed to download template');
        }
    };

    const handleImport = async () => {
        if (!file) {
            setError('Please select a file first');
            return;
        }

        setIsUploading(true);
        setError(null);
        setUploadResult(null);

        try {
            const result = await employeeService.importEmployees(file);

            if (result.success) {
                setUploadResult(result.data);
                if (onImportSuccess) {
                    onImportSuccess();
                }
            } else {
                setError(result.error);
            }
        } catch (err) {
            setError('An unexpected error occurred during import');
        } finally {
            setIsUploading(false);
        }
    };

    const resetState = () => {
        setFile(null);
        setIsUploading(false);
        setUploadResult(null);
        setError(null);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-md overflow-hidden border border-slate-200 dark:border-slate-800">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
                    <div>
                        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Import Employees</h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                            Upload CSV file to bulk import
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Template Download Section */}
                    <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-4 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full">
                                <FileSpreadsheet className="h-5 w-5 text-green-600 dark:text-green-500" />
                            </div>
                            <div>
                                <span className="text-sm font-medium text-slate-900 dark:text-white block">Sample Template</span>
                                <span className="text-xs text-slate-500 dark:text-slate-400">Required format</span>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="gap-2 h-9">
                            <Download className="h-4 w-4" />
                            Download
                        </Button>
                    </div>

                    {/* File Upload Section */}
                    <div className="relative">
                        <div className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center gap-3 transition-colors ${file
                            ? 'border-blue-500 bg-blue-50/50 dark:border-blue-500/50 dark:bg-blue-900/20'
                            : 'border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                            } `}>
                            <div className={`p-4 rounded-full ${file ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'} `}>
                                <Upload className="h-8 w-8" />
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-medium text-slate-900 dark:text-white">
                                    {file ? file.name : 'Click to select file'}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    Supports .csv
                                </p>
                            </div>
                        </div>
                        <input
                            type="file"
                            accept=".csv"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            onChange={handleFileChange}
                            disabled={isUploading}
                        />
                    </div>

                    {/* Messages */}
                    {error && (
                        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                            <div className="text-sm font-medium">{error}</div>
                        </div>
                    )}

                    {uploadResult && (
                        <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-500 " />
                                <span className="font-semibold text-sm">Import Completed</span>
                            </div>
                            <div className="text-sm space-y-1 pl-7">
                                <p>Total Processed: {uploadResult.total}</p>
                                <p className="font-medium text-green-700 dark:text-green-400">Successfully Imported: {uploadResult.imported}</p>
                                {uploadResult.failed > 0 && (
                                    <div className="text-amber-700 dark:text-amber-500 mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
                                        <p className="font-semibold">Failed: {uploadResult.failed}</p>
                                        <ul className="list-disc pl-4 text-xs mt-1 max-h-24 overflow-y-auto">
                                            {uploadResult.errors.map((err, i) => (
                                                <li key={i}>{err}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex justify-end gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleClose}
                        disabled={isUploading}
                    >
                        Close
                    </Button>
                    <Button
                        type="button"
                        onClick={handleImport}
                        disabled={!file || isUploading || (uploadResult && uploadResult.failed === 0 && uploadResult.imported > 0)}
                        className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                    >
                        {isUploading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Importing...
                            </>
                        ) : (
                            'Import Employees'
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default EmployeeImportModal;
